import moment from "moment-timezone";

export type PaymentInfoAvailability = {
  notApplicable: boolean;
  tooltip?: string;
};

export const DEFAULT_PAYMENT_INFO_MAX_AGE_DAYS = 60;

const getShiftPaymentInfoMoment = (shift: any) => {
  const raw =
    shift?.officialStartTime ??
    shift?.startTime ??
    shift?.actualStartTime ??
    shift?.endTime;

  if (!raw) return null;

  const parsed = moment(raw);
  if (!parsed.isValid()) return null;

  const tz = shift?.shiftTimeZone;
  if (tz) {
    try {
      return parsed.tz(tz);
    } catch (_e) {
      // ignore timezone errors
    }
  }

  return parsed;
};

const formatNotApplicableTooltip = (maxAgeDays: number) =>
  `Payment info is not available for shifts prior to launch or older than ${maxAgeDays} days.`;

export const isPaymentInfoNotApplicable = (
  shift: any,
  now: Date = new Date(),
  options?: {
    maxAgeDays?: number;
    launchDateIso?: string | null | undefined;
  }
) => {
  const shiftMoment = getShiftPaymentInfoMoment(shift);
  if (!shiftMoment) return false;

  const maxAgeDays =
    typeof options?.maxAgeDays === "number"
      ? options.maxAgeDays
      : DEFAULT_PAYMENT_INFO_MAX_AGE_DAYS;

  const nowMoment = moment(now);
  const isOlderThanWindow = nowMoment.diff(shiftMoment, "days") >= maxAgeDays;

  const shiftLaunchRaw = shift?.shiftPaidFeatureLaunchDate;

  const launchRaw =
    typeof options?.launchDateIso === "string"
      ? options.launchDateIso
      : typeof shiftLaunchRaw === "string" && shiftLaunchRaw.trim().length > 0
        ? shiftLaunchRaw
        : null;

  const launchMoment = launchRaw ? moment.utc(launchRaw) : null;
  const isPreLaunch =
    launchMoment && launchMoment.isValid()
      ? shiftMoment.clone().utc().isBefore(launchMoment)
      : false;

  return isOlderThanWindow || isPreLaunch;
};

export const getPaymentInfoAvailability = (
  shift: any,
  now: Date = new Date(),
  options?: {
    maxAgeDays?: number;
    launchDateIso?: string | null | undefined;
  }
): PaymentInfoAvailability => {
  const maxAgeDays =
    typeof options?.maxAgeDays === "number"
      ? options.maxAgeDays
      : DEFAULT_PAYMENT_INFO_MAX_AGE_DAYS;

  const notApplicable = isPaymentInfoNotApplicable(shift, now, options);
  return {
    notApplicable,
    tooltip: notApplicable ? formatNotApplicableTooltip(maxAgeDays) : undefined,
  };
};
